import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  Stethoscope, 
  MessageSquare, 
  Image, 
  FileText, 
  Users, 
  Shield,
  ArrowRight,
  Activity,
  Heart,
  Brain,
  ArrowLeft
} from "lucide-react";

const DiagnoseLanding = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: <MessageSquare className="w-6 h-6" />,
      title: "Interactive Diagnosis",
      description: "Chat with Dr. Veena for step-by-step clinical reasoning"
    },
    {
      icon: <Image className="w-6 h-6" />,
      title: "Image Analysis",
      description: "Upload images for clinical sign detection"
    },
    {
      icon: <FileText className="w-6 h-6" />,
      title: "Report Upload",
      description: "Share lab reports and prescriptions for accurate diagnosis"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Doctor Consultation",
      description: "Connect with specialists for in-person or teleconsult"
    }
  ];

  const pathyTypes = [
    { name: "Allopathy", color: "text-blue-400" },
    { name: "Ayurveda", color: "text-green-500" },
    { name: "Homeopathy", color: "text-purple-400" },
    { name: "Naturopathy", color: "text-yellow-400" },
    { name: "Yoga", color: "text-orange-400" },
    { name: "Unani", color: "text-emerald-400" }
  ];

  return (
    <div className="min-h-screen bg-[#050505] relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(0,255,148,0.1),transparent_50%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(6,182,212,0.05),transparent_50%)]" />
      
      {/* Header */}
      <header className="relative z-10 flex items-center justify-between px-6 py-4 border-b border-white/5">
        <div className="flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate("/dashboard")}
            className="text-slate-400 hover:text-white hover:bg-white/10 mr-2"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="w-10 h-10 rounded-xl bg-[#00FF94]/20 flex items-center justify-center">
            <Stethoscope className="w-5 h-5 text-[#00FF94]" />
          </div>
          <div>
            <span className="text-white font-bold text-lg tracking-tight">Let's</span>
            <span className="text-[#00FF94] font-bold text-lg tracking-tight"> Diagnose</span>
          </div>
        </div>
        <Button 
          data-testid="header-start-diagnosis-btn"
          onClick={() => navigate("/diagnose-chat")}
          className="bg-[#00FF94] text-black font-semibold hover:bg-[#00CC76] shadow-[0_0_15px_rgba(0,255,148,0.3)]"
        >
          Start Diagnosis
        </Button>
      </header>

      {/* Hero Section */}
      <main className="relative z-10 max-w-7xl mx-auto px-6 pt-20 pb-32">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#00FF94]/10 border border-[#00FF94]/30 mb-6">
            <Activity className="w-4 h-4 text-[#00FF94]" />
            <span className="text-[#00FF94] text-sm font-medium">AI-Powered Diagnosis Support</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white tracking-tight mb-6">
            Meet <span className="text-[#00FF94] neon-text">Dr. Veena</span>
            <br />
            Your AI Diagnosis Partner
          </h1>
          
          <p className="text-lg md:text-xl text-slate-400 max-w-2xl mx-auto mb-10 leading-relaxed">
            Experience structured clinical reasoning through interactive conversations. 
            Get multi-pathy treatment protocols and connect with specialists.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              data-testid="hero-start-diagnosis-btn"
              onClick={() => navigate("/diagnose-chat")}
              size="lg"
              className="bg-[#00FF94] text-black font-bold hover:bg-[#00CC76] shadow-[0_0_20px_rgba(0,255,148,0.4)] px-8 py-6 text-lg animate-glow"
            >
              Start Diagnosis
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button 
              data-testid="find-doctors-btn"
              onClick={() => navigate("/consult-doctor")}
              variant="outline"
              size="lg"
              className="border-[#00FF94]/50 text-[#00FF94] hover:bg-[#00FF94]/10 px-8 py-6 text-lg"
            >
              <Users className="w-5 h-5 mr-2" />
              Find Doctors
            </Button>
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
          {features.map((feature, index) => (
            <div 
              key={index}
              className="bg-[#0F1115]/80 backdrop-blur-md border border-white/5 rounded-xl p-6 hover:border-[#00FF94]/30 transition-all duration-300 hover:shadow-[0_0_30px_rgba(0,255,148,0.1)]"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="w-12 h-12 rounded-lg bg-[#00FF94]/10 flex items-center justify-center mb-4 text-[#00FF94]">
                {feature.icon}
              </div>
              <h3 className="text-white font-semibold text-lg mb-2">{feature.title}</h3>
              <p className="text-slate-400 text-sm leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>

        {/* Multi-pathy Section */}
        <div className="text-center mb-16">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4 tracking-tight">
            <span className="text-[#00FF94]">Multi-Pathy</span> Treatment Protocols
          </h2>
          <p className="text-slate-400 mb-8 max-w-xl mx-auto">
            Get comprehensive treatment recommendations across multiple medical systems
          </p>
          
          <div className="flex flex-wrap items-center justify-center gap-4">
            {pathyTypes.map((pathy, index) => (
              <div 
                key={index}
                className={`px-5 py-2.5 rounded-full bg-white/5 border border-white/10 ${pathy.color} font-medium text-sm hover:border-white/20 transition-colors`}
              >
                {pathy.name}
              </div>
            ))}
          </div>
        </div>

        {/* Stats Section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-[#0F1115]/60 rounded-2xl p-8 text-center border border-white/5">
            <Heart className="w-8 h-8 text-red-400 mx-auto mb-4" />
            <div className="text-3xl font-bold text-white mb-2">15+</div>
            <div className="text-slate-400 text-sm">Specialist Doctors</div>
          </div>
          <div className="bg-[#0F1115]/60 rounded-2xl p-8 text-center border border-white/5">
            <Brain className="w-8 h-8 text-[#00FF94] mx-auto mb-4" />
            <div className="text-3xl font-bold text-white mb-2">AI-Powered</div>
            <div className="text-slate-400 text-sm">Clinical Reasoning</div>
          </div>
          <div className="bg-[#0F1115]/60 rounded-2xl p-8 text-center border border-white/5">
            <Shield className="w-8 h-8 text-blue-400 mx-auto mb-4" />
            <div className="text-3xl font-bold text-white mb-2">Secure</div>
            <div className="text-slate-400 text-sm">Data Protection</div>
          </div>
        </div>
      </main>

      {/* Footer disclaimer */}
      <footer className="relative z-10 border-t border-white/5 py-6 px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-slate-500 text-sm text-center md:text-left">
            ⚠️ This is a diagnosis support system. Please consult a registered healthcare professional for final diagnosis.
          </p>
          <div className="text-slate-600 text-sm">
            Powered by <span className="text-[#00FF94]">MedPlexity AI</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DiagnoseLanding;
